import { useCallback, useEffect, useRef, useState } from "react";

import { CardFundingOptionsService } from "../services/cardFundingOptions.service";
import type { CardFundingOptions } from "../types/cardFundingOptions.types";

interface Snapshot {
  cardId: string;
  options: CardFundingOptions | null;
  hasError: boolean;
}

export interface UseCardFundingOptionsResult {
  options: CardFundingOptions | null;
  isLoading: boolean;
  hasError: boolean;
  reload: () => void;
}

/**
 * Every way to load one card, for the card's organization (so staff see a client's card wallet). Read with
 * the card and again whenever "Cargar" opens, like the COP terms.
 */
export function useCardFundingOptions(
  cardId: string | null,
  enabled = true,
  /** The card's organization, only for the fallback against a backend without the route. */
  organizationId: string | null = null,
): UseCardFundingOptionsResult {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const key = enabled && cardId ? cardId : null;
  // Read at request time, not a dependency: the card (and its organization) arrives after the first read,
  // and that must not fire a second one.
  const organizationRef = useRef(organizationId);
  useEffect(() => {
    organizationRef.current = organizationId;
  }, [organizationId]);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    CardFundingOptionsService.forCard(key, organizationRef.current)
      .then((options) => {
        if (!cancelled) setSnapshot({ cardId: key, options, hasError: false });
      })
      .catch(() => {
        if (!cancelled) setSnapshot({ cardId: key, options: null, hasError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [key, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  const current = key && snapshot?.cardId === key ? snapshot : null;

  return {
    options: current?.options ?? null,
    isLoading: !!key && current === null,
    hasError: current?.hasError ?? false,
    reload,
  };
}
